import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useAnchoredPosition } from '@/hooks/useAnchoredPosition'
import { cn } from '@/lib/utils'
import { containsTarget } from '@/lib/dom'

interface NavDropdownLink {
  label: string
  href: string
}

interface NavDropdownProps {
  label: string
  links: readonly NavDropdownLink[]
}

export function NavDropdown({ label, links }: NavDropdownProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const coords = useAnchoredPosition(open, triggerRef, panelRef, { panelAnchorRatio: 0.35 })

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containsTarget(rootRef.current, e.target)) return
      if (containsTarget(panelRef.current, e.target)) return
      setOpen(false)
    }
    if (!open) return
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const handleEnter = () => setOpen(true)
  const handleLeave = (e: React.MouseEvent) => {
    if (containsTarget(panelRef.current, e.relatedTarget)) return
    setOpen(false)
  }

  const panel = open ? (
    <div
      ref={panelRef}
      role="menu"
      className={cn(
        'fixed z-[110] min-w-[200px] rounded-xl border border-neutral-200 bg-white py-2 shadow-xl',
        !coords.ready && 'invisible',
      )}
      style={{ top: coords.top, left: coords.left }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setOpen(false)}
    >
      {links.map((link) => (
        <Link
          key={link.href}
          to={link.href}
          onClick={() => setOpen(false)}
          className="block cursor-pointer px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
        >
          {link.label}
        </Link>
      ))}
    </div>
  ) : null

  return (
    <div
      ref={rootRef}
      className="relative shrink-0"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'relative cursor-pointer whitespace-nowrap px-2 py-2 text-[13px] font-semibold transition-colors xl:px-2.5 xl:text-sm',
          open ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600',
          open && 'after:absolute after:bottom-0 after:left-1 after:right-1 after:h-0.5 after:rounded-full after:bg-primary-500',
        )}
      >
        {label}
      </button>

      {open && <span className="absolute left-1/2 top-full z-90 h-2 w-16 -translate-x-1/2" aria-hidden />}

      {typeof document !== 'undefined' && panel ? createPortal(panel, document.body) : null}
    </div>
  )
}
